import React, { useState, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Breadcrumb,
  message,
  Input,
  Space,
  Button,
  DatePicker,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getConnectedUsers } from "./ducks/action";
import Headings from "../../atoms/Headigs";
import ListCard from "../../molecules/ListCard";
import ExportFeature from "../../molecules/ExportFeature";
import { exportConnectedUsersList, getConnectedUsersDownloadUrl } from "./ducks/services";
import { useKeycloak } from '@react-keycloak/web';
import axios from "../../../configs/axiosIntereptor";
import moment from "moment";

const ListCol = [
  {
    title: "User Name",
    dataIndex: "UserName",
    key: "UserName",
  },
  {
    title: "Source Country",
    dataIndex: "CallingCountry",
    key: "CallingCountry",
  },
  {
    title: "Subscription Type",
    dataIndex: "SubscriptionType",
    key: "SubscriptionType",
  },
  {
    title: "Total Connection",
    dataIndex: "total_connection",
    key: "total_connection",
  },
  {
    title: "Total Bandwidth",
    dataIndex: "total_bandwidth",
    key: "total_bandwidth",
    render: (value) => {      
      if (value == null || value === undefined || value === '') {
        return '-';
      }
      const mbValue = Number(value);
      if (isNaN(mbValue)) {
        return value;
      }
      if (mbValue >= 1024 * 1024) {
        const tbValue = (mbValue / (1024 * 1024)).toFixed(2);
        return `${tbValue} TB`;
      }
      if (mbValue >= 1024) {
        const gbValue = (mbValue / 1024).toFixed(2);
        return `${gbValue} GB`;
      }
      return `${mbValue.toFixed(2)} MB`;
    },
  },
];

const options = { year: "numeric", month: "long" };
const formattedDate = new Date().toLocaleString("en-US", options);
const PAGE_SIZE = parseInt(process.env.NO_OF_USERS_IN_TABLE || '100', 10);

const ConnectedUsers = () => {
  const { keycloak } = useKeycloak();
  const [listData, setListData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const parseNumeric = (value) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const buildApiFiltersFromTableFilters = (filters = {}) => {
    const apiFilters = {};
    Object.keys(filters).forEach((key) => {
      const rawValue = Array.isArray(filters[key]) ? filters[key][0] : filters[key];
      if (rawValue === undefined || rawValue === null || rawValue === "") return;

      if (key === "total_connection") {
        const [min, max] = String(rawValue).split("|");
        const minVal = parseNumeric(min);
        const maxVal = parseNumeric(max);
        if (minVal !== null) apiFilters.total_connection_min = String(minVal);
        if (maxVal !== null) apiFilters.total_connection_max = String(maxVal);
        return;
      }

      if (key === "total_bandwidth") {
        const [min, max, unit] = String(rawValue).split("|");
        const minVal = parseNumeric(min);
        const maxVal = parseNumeric(max);
        if (minVal !== null) apiFilters.total_bandwidth_min = String(minVal);
        if (maxVal !== null) apiFilters.total_bandwidth_max = String(maxVal);
        if (unit) apiFilters.total_bandwidth_unit = unit;
        return;
      }

      apiFilters[key] = rawValue;
    });
    return apiFilters;
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            confirm();
            close();
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
              close();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => close()}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    filteredValue: columnFilters[dataIndex] ? [columnFilters[dataIndex]] : null,
  });

  const searchableColumns = [
    "UserName",
    "CallingCountry",
    "SubscriptionType",
  ];

  const getRangeFilterProps = (rangeKey, includeUnit = false) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => {
      const [rawMin = "", rawMax = "", rawUnit = "MB"] = String(selectedKeys?.[0] || "").split("|");
      const minValue = rawMin;
      const maxValue = rawMax;
      const unitValue = includeUnit ? rawUnit || "MB" : "MB";

      return (
        <div style={{ padding: 8, width: 240 }} onKeyDown={(e) => e.stopPropagation()}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Input
              type="number"
              placeholder="Min"
              value={minValue}
              onChange={(e) => {
                const next = includeUnit
                  ? `${e.target.value || ""}|${maxValue || ""}|${unitValue}`
                  : `${e.target.value || ""}|${maxValue || ""}`;
                setSelectedKeys(next === "|" || next === "||MB" ? [] : [next]);
              }}
            />
            <Input
              type="number"
              placeholder="Max"
              value={maxValue}
              onChange={(e) => {
                const next = includeUnit
                  ? `${minValue || ""}|${e.target.value || ""}|${unitValue}`
                  : `${minValue || ""}|${e.target.value || ""}`;
                setSelectedKeys(next === "|" || next === "||MB" ? [] : [next]);
              }}
            />
            {includeUnit && (
              <select
                value={unitValue}
                onChange={(e) => {
                  const selectedUnit = e.target.value;
                  const next = `${minValue || ""}|${maxValue || ""}|${selectedUnit}`;
                  setSelectedKeys(next === "||MB" ? [] : [next]);
                }}
                style={{ width: "100%", height: 32 }}
              >
                <option value="MB">MB</option>
                <option value="GB">GB</option>
              </select>
            )}
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  confirm();
                  close();
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                onClick={() => {
                  clearFilters();
                  confirm();
                }}
              >
                Reset
              </Button>
            </Space>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    filteredValue: columnFilters[rangeKey] ? [columnFilters[rangeKey]] : null,
  });

  const enhancedListColumns = ListCol.map((column) => {
    if (searchableColumns.includes(column.dataIndex)) {
      return { ...column, ...getColumnSearchProps(column.dataIndex) };
    }
    if (column.dataIndex === "total_connection") {
      return { ...column, ...getRangeFilterProps("total_connection", false) };
    }
    if (column.dataIndex === "total_bandwidth") {
      return { ...column, ...getRangeFilterProps("total_bandwidth", true) };
    }
    return column;
  });

  const getDescription = () => {
    if (dateRange && dateRange.length === 2 && startDate && endDate) {
      const start = moment(startDate).format('MMM DD, YYYY');
      const end = moment(endDate).format('MMM DD, YYYY');
      return `Connected users from ${start} to ${end}`;
    }
    return `Connected users in ${formattedDate}`;
  };

  const onTableChange = (pagination, filters, sorter) => {
    setLoading(true);
    const activeFilters = {};
    Object.keys(filters || {}).forEach((key) => {
      if (filters[key] && filters[key].length > 0 && filters[key][0] !== undefined) {
        activeFilters[key] = filters[key][0];
      }
    });
    const apiFilters = buildApiFiltersFromTableFilters(activeFilters);
    setColumnFilters(activeFilters);
    setPage(pagination.current);
    getConnectedUsers(
      pagination.current,
      PAGE_SIZE,
      startDate,
      endDate,
      apiFilters
    ).then((response) => {
      if (response?.data?.header?.code == 200) {
        setListData(response?.data?.body);
      } else {
        message.error(response?.data?.header?.message || "Failed to fetch data");
      }
      setLoading(false);
    }).catch(handleError);
  };

  const handleDateChange = (dates, dateStrings) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const daysDiff = end.diff(start, 'days');
      
      if (daysDiff > 30) {
        message.error("Date range cannot exceed 30 days. Please select a smaller range.");
        setDateRange([start, null]);
        return;
      }
      
      setDateRange(dates);
      setStartDate(dateStrings[0] || null);
      setEndDate(dateStrings[1] || null);
      setPage(1);
      setLoading(true);
      getConnectedUsers(
        1,
        PAGE_SIZE,
        dateStrings[0],
        dateStrings[1],
        buildApiFiltersFromTableFilters(columnFilters)
      ).then((response) => {
        if (response?.data?.header?.code == 200) {
          setListData(response?.data?.body);
        } else {
          message.error(response?.data?.header?.message || "Failed to fetch data");
        }
        setLoading(false);
      }).catch(handleError);
    } else {
      setDateRange(null);
      setStartDate(null);
      setEndDate(null);
      setPage(1);
      setLoading(true);
      getConnectedUsers(
        1,
        PAGE_SIZE,
        null,
        null,
        buildApiFiltersFromTableFilters(columnFilters)
      ).then((response) => {
        if (response?.data?.header?.code == 200) {
          setListData(response?.data?.body);
        } else {
          message.error(response?.data?.header?.message || "Failed to fetch data");
        }
        setLoading(false);
      }).catch(handleError);
    }
  };

  const disabledDate = (current) => {
    if (!current) return false;
    
    const currentDate = moment(current);
    const today = moment().endOf('day');
    const threeMonthsAgo = moment().subtract(3, 'months').startOf('day');
    
    // Disable dates after today
    if (currentDate.isAfter(today)) {
      return true;
    }
    
    // Disable dates before 3 months ago
    if (currentDate.isBefore(threeMonthsAgo)) {
      return true;
    }
    
    // If first date is selected, limit second date to within 30 days
    if (dateRange && dateRange.length === 1 && dateRange[0]) {
      const selectedDate = moment(dateRange[0]);
      const thirtyDaysAfter = selectedDate.clone().add(30, 'days').endOf('day');
      const thirtyDaysBefore = selectedDate.clone().subtract(30, 'days').startOf('day');
      
      // Disable dates more than 30 days away from selected date
      if (currentDate.isAfter(thirtyDaysAfter) || currentDate.isBefore(thirtyDaysBefore)) {
        return true;
      }
    }
    
    return false;
  };

  useEffect(() => {
    setLoading(true);
    getConnectedUsers(1, PAGE_SIZE, null, null, {}).then((response) => {
      if (response?.data?.header?.code == 200) {
        setListData(response?.data?.body);
      } else {
        message.error(response?.data?.header?.message || "Failed to fetch data");
      }
      setLoading(false);
    }).catch(handleError);
  }, []);
  const handleError = (error) => {
    const messageText =
      error?.response?.data?.header?.message ||
      error?.message ||
      "Something went wrong";
    message.error(messageText);
    setLoading(false);
  };

  // Smart Export handler: backend decides direct CSV vs S3 export
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = keycloak?.token;
      const exportQueryParams = new URLSearchParams({
        fromDate: startDate || "",
        toDate: endDate || "",
      });
      const exportFilters = buildApiFiltersFromTableFilters(columnFilters);
      Object.keys(exportFilters).forEach((filterKey) => {
        exportQueryParams.set(filterKey, exportFilters[filterKey]);
      });
      const queryString = exportQueryParams.toString();

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/billing/export`,
        { exportType: "connected_users", queryString },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob", timeout: 10 * 60 * 1000 }
      );

      const contentType = res?.headers?.["content-type"] || "";
      const isJson = contentType.includes("application/json");

      if (isJson) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (json?.body?.mode === "queued") {
          message.success("Export queued. Please check Files Exported.");
          setExportLoading(false);
          return { queued: true };
        }
        message.error(json?.header?.message || "Unexpected error");
        setExportLoading(false);
        return;
      }

      const contentDisposition = res.headers['content-disposition'];
      const fileName = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/["']/g, '')
        : `connected_users_${Date.now()}.csv`;

      const blob = new Blob([res.data], { type: 'text/csv' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.setAttribute('download', fileName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      setExportLoading(false);
    } catch (err) {
      console.error('Export Error:', err);
      message.error('Failed to export data');
      setExportLoading(false);
    }
  };

  // Export All is now handled by backend smart-export endpoint.

  return (
    <Card className="design-card">
      <Row gutter={[24, 30]}>
        {/* <Col span={24}>
        <Breadcrumb>
          <Breadcrumb.Item>Disrupt</Breadcrumb.Item>
          <Breadcrumb.Item>Connected Users</Breadcrumb.Item>
        </Breadcrumb>
      </Col> */}
        <Col span={12}>
          <Headings
            title="Connected Users "
            titleSize={2}
            description={getDescription()}
            space={5}
            descClass="mb-0 c-gray"
          />
        </Col>
        <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            <ExportFeature
              onExport={handleExport}
              getExportList={exportConnectedUsersList}
              getDownloadUrl={getConnectedUsersDownloadUrl}
              rowData={listData}
              effectiveTotalRecords={listData?.total_users || 0}
              exportLoading={exportLoading}
              modalTitle="Exported Connected User Files"
              exportType="connected_users"
            />
          <Space>
            <span style={{ marginRight: '8px' }}></span>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
              style={{ width: '280px' }}
              allowClear
            />
          </Space>
        </Col>
        <Col span={24}>
          <ListCard
            ListCol={enhancedListColumns}
            ListData={listData?.connectedData}
            total={listData?.total_users}
            filteredRecords={listData?.total_users}
            paginationSummaryType="simple"
            listClass="nospace-card"
            onChange={onTableChange}
            loading={loading}
            pagination={{
              total: listData?.total_users,
              showSizeChanger: false,
              current: page,
              pageSize: PAGE_SIZE,
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default ConnectedUsers;
